// L'EVEIL DES BANDES DU SOCLE, une seule implementation : le dock
// (`packages/shell/src/Dock.tsx`), le rail de suivi, les etapes et la
// selection de la rangee 3 d'une fiche (`docs/maquettes/atelier-2610/README.md`,
// section 5). Grisee au repos (opacite 50 %), pleine au survol ou au focus
// clavier (300 ms), regrisee 1,2 s apres la sortie de la souris, en 2 s. Seul
// le CONTENU s'attenue ; le fond et les filets restent nets.
//
// DEUX FACONS DE S'EN SERVIR : poser `gestes` sur la bande (le cas courant),
// ou appeler `eveiller` et `endormir` depuis ses propres gestes, quand la bande
// a d'autres regles de survol (le dock : ouverture differee, sorties fantomes).
// Dans les deux cas, le tempo et les classes sont ceux d'ici.
import { useEffect, useRef, useState } from 'react';
import type { FocusEvent } from 'react';
import { cn } from './cn.js';

const DELAI_REGRISEMENT_MS = 1200;

export function useEveil(forcee = false) {
  const [eveillee, setEveillee] = useState(false);
  const minuterie = useRef<number | undefined>(undefined);
  const survolee = useRef(false);
  const annuler = (): void => {
    if (minuterie.current != null) window.clearTimeout(minuterie.current);
    minuterie.current = undefined;
  };
  const eveiller = (): void => {
    annuler();
    setEveillee(true);
  };
  const endormir = (): void => {
    annuler();
    minuterie.current = window.setTimeout(() => setEveillee(false), DELAI_REGRISEMENT_MS);
  };
  useEffect(() => annuler, []);
  /* `forcee` : la bande reste pleine tant que ce qu'elle commande est ouvert. */
  const pleine = eveillee || forcee;
  return {
    /** Vrai tant que la bande est pleine (eveillee, ou forcee). */
    eveillee: pleine,
    /** Eveille tout de suite, et annule un regrisement en attente. */
    eveiller,
    /** Regrise apres le delai (1,2 s). */
    endormir,
    /** Les gestes qui eveillent, a poser sur la bande entiere. */
    gestes: {
      onMouseEnter: (): void => {
        survolee.current = true;
        eveiller();
      },
      // Comme le dock : une vraie sortie de la souris regrise toujours, meme
      // si le clic vient de laisser le focus a l'interieur.
      onMouseLeave: (): void => {
        survolee.current = false;
        endormir();
      },
      onFocusCapture: eveiller,
      onBlurCapture: (event: FocusEvent<HTMLElement>): void => {
        if (survolee.current) return;
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        endormir();
      },
    },
    /** Les classes du CONTENU qui s'attenue. */
    fondu: cn(
      'transition-opacity ease-in-out',
      pleine ? 'opacity-100 duration-300' : 'opacity-50 duration-[2000ms]',
    ),
  };
}
